import { createSlice } from "@reduxjs/toolkit";
import {
  AreaTypeEnum,
  DropDownCategoryEnumModel,
  ICommonDataModel,
  ICustomUserDataModel,
} from "../../../types/types";
import { PURGE } from "redux-persist";
import {
  commonDataLoading,
  commonDataLoaded,
  commonDataError,
  customUserDataLoading,
  customUserDataLoaded,
  customUserDataError,
  addCategoryVariablePending,
  addCategoryVariableFulfilled,
  addCategoryVariableRejected,
  updateCategoryVariablePending,
  updateCategoryVariableRejected,
  updateCategoryVariableFulfilled,
  deleteCategoryVariablePending,
  deleteCategoryVariableRejected,
  deleteCategoryVariableFulfilled,
  hideCategoryVariablePending,
  hideCategoryVariableFulfilled,
  updateAreaTypePreferencePending,
  updateAreaTypePreferenceFulfilled,
  updateAreaTypePreferenceRejected,
  duplicateCandidatesLoading,
  duplicateCandidatesLoaded,
  duplicateCandidatesError,
  resolveDuplicateCandidatePending,
  resolveDuplicateCandidateFulfilled,
  resolveDuplicateCandidateRejected,
  mergeCreatedEntities,
} from "./commonStateActions";
import { OperationStatus } from "../../../types/models";

interface ICustomUserDataState {
  loading: boolean;
  error: string | null;
  data: ICustomUserDataModel | null;
  loaded: boolean;
}

interface IAddCategoryVariableState {
  pending: boolean;
  error: string | null;
  fulfilled: boolean;
  category: DropDownCategoryEnumModel | undefined;
}

interface IUpdateCategoryVariableState {
  status?: OperationStatus | undefined;
  category?: DropDownCategoryEnumModel | undefined;
  variableId?: number | undefined;
}
interface IHideCategoryVariableState {
  status?: OperationStatus | undefined;
  category?: DropDownCategoryEnumModel | undefined;
  variableId?: number | undefined;
  hide?: boolean | undefined;
}

interface IDeleteCategoryVariableState {
  status?: OperationStatus | undefined;
  category?: DropDownCategoryEnumModel | undefined;
  message?: string | undefined;
}

interface IDuplicateCandidatesState {
  loading: boolean;
  error: string | null;
  data: any[] | null;
  resolving: boolean;
  resolvingId: number | null;
  resolveError: string | null;
}

interface CommonState {
  loading: boolean;
  error: string | null;
  data: ICommonDataModel | null;
  loaded: boolean;
  userData: ICustomUserDataState;
  addCategoryVariableState: IAddCategoryVariableState;
  updateCategoryVariableState: IUpdateCategoryVariableState;
  deleteCategoryVariableState: IDeleteCategoryVariableState;
  hideCategoryVariableState?: IHideCategoryVariableState;
  areaTypePreferenceState: {
    loading: boolean;
    error: string | null;
  };
  duplicateCandidates: IDuplicateCandidatesState;
}

const initialState: CommonState = {
  loading: false,
  error: null,
  data: null,
  loaded: false,
  userData: {
    loading: false,
    error: null,
    data: null,
    loaded: false,
  },
  addCategoryVariableState: {
    error: null,
    fulfilled: false,
    pending: false,
    category: undefined,
  },
  updateCategoryVariableState: {},
  deleteCategoryVariableState: {},
  areaTypePreferenceState: {
    loading: false,
    error: null,
  },
  duplicateCandidates: {
    loading: false,
    error: null,
    data: null,
    resolving: false,
    resolvingId: null,
    resolveError: null,
  },
};

const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => initialState)
      .addCase(commonDataLoading, (state) => {
        state.loading = true;
        state.error = null;
        state.loaded = false;
      })
      .addCase(customUserDataLoading, (state) => {
        state.userData.loading = true;
        state.userData.error = null;
        state.userData.loaded = false;
      })
      .addCase(commonDataLoaded, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.loaded = true;
        state.error = null;
      })
      .addCase(customUserDataLoaded, (state, action) => {
        state.userData.loading = false;
        state.userData.data = action.payload;
        state.userData.loaded = true;
        state.userData.error = null;
      })
      .addCase(mergeCreatedEntities, (state, action) => {
        if (!state.userData.data) {
          state.userData.data = {};
        }
        const { payload } = action;
        // Merge specialTags
        if (payload.specialTags?.length) {
          state.userData.data.specialTags = [
            ...(state.userData.data.specialTags || []),
            ...payload.specialTags,
          ];
        }

        // Merge specialGroups
        if (payload.specialGroups?.length) {
          state.userData.data.specialGroups = [
            ...(state.userData.data.specialGroups || []),
            ...payload.specialGroups,
          ];
        }

        if (payload.area) {
          state.userData.data.areas = [
            ...(state.userData.data.areas || []),
            payload.area,
          ];
        }
        //coagents
        if (payload.coAgents?.length) {
          state.userData.data.coAgents = [
            ...(state.userData.data.coAgents || []),
            ...payload.coAgents,
          ];
        }
      })
      .addCase(commonDataError, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.loaded = false;
      })
      .addCase(customUserDataError, (state, action) => {
        state.userData.loading = false;
        state.userData.error = action.payload;
        state.userData.loaded = false;
      })
      .addCase(addCategoryVariablePending, (state, action) => {
        state.addCategoryVariableState.pending = true;
        state.addCategoryVariableState.error = null;
        state.addCategoryVariableState.fulfilled = false;
      })
      .addCase(addCategoryVariableRejected, (state, action) => {
        state.addCategoryVariableState.pending = false;
        state.addCategoryVariableState.error = action.payload;
      })
      .addCase(addCategoryVariableFulfilled, (state, action) => {
        state.addCategoryVariableState.pending = false;
        switch (action.payload.category) {
          case DropDownCategoryEnumModel.Reason:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.reasons = [
                ...(state.userData.data.reasons || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.Source:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.sources = [
                ...(state.userData.data.sources || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.Area:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.areas = [
                ...(state.userData.data.areas || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.CoAgent:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.coAgents = [
                ...(state.userData.data.coAgents || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.TransactionType:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.transactionTypes = [
                ...(state.userData.data.transactionTypes || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.SpecialGroup:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.specialGroups = [
                ...(state.userData.data.specialGroups || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.SpecialTag:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.specialTags = [
                ...(state.userData.data.specialTags || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
          case DropDownCategoryEnumModel.ReferralOutType:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            if (state.userData.data) {
              state.userData.data.referralOutTypes = [
                ...(state.userData.data.referralOutTypes || []),
                {
                  id: action.payload.variableId,
                  name: action.payload.name,
                },
              ];
            }
            break;
        }

        state.addCategoryVariableState.fulfilled = true;
        state.addCategoryVariableState.category = action.payload.category;
      })
      .addCase(updateCategoryVariablePending, (state, action) => {
        state.updateCategoryVariableState.status = OperationStatus.Pending;
        state.updateCategoryVariableState.category = action.payload.category;
        state.updateCategoryVariableState.variableId =
          action.payload.variableId;
      })
      .addCase(updateCategoryVariableRejected, (state, action) => {
        state.updateCategoryVariableState.status = OperationStatus.Failed;
        state.updateCategoryVariableState.category = undefined;
        state.updateCategoryVariableState.variableId = undefined;
      })
      .addCase(updateCategoryVariableFulfilled, (state, action) => {
        switch (action.payload.category) {
          case DropDownCategoryEnumModel.Reason:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let reasonIndex = state.userData.data.reasons?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (reasonIndex !== undefined && reasonIndex >= 0) {
              state.userData.data.reasons![reasonIndex].name =
                action.payload.name;
            }
            break;
          case DropDownCategoryEnumModel.Source:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let sourceIndex = state.userData.data.sources?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (sourceIndex !== undefined && sourceIndex >= 0) {
              state.userData.data.sources![sourceIndex].name =
                action.payload.name;
            }
            break;
          case DropDownCategoryEnumModel.TransactionType:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let typeIndex = state.userData.data.transactionTypes?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (typeIndex !== undefined && typeIndex >= 0) {
              state.userData.data.transactionTypes![typeIndex].name =
                action.payload.name;
            }
            break;
          case DropDownCategoryEnumModel.Area:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let areaIndex = state.userData.data.areas?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (areaIndex !== undefined && areaIndex >= 0) {
              state.userData.data.areas![areaIndex].name = action.payload.name;
            }
            break;
          case DropDownCategoryEnumModel.CoAgent:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let coAgentIndex = state.userData.data.coAgents?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (coAgentIndex !== undefined && coAgentIndex >= 0) {
              state.userData.data.coAgents![coAgentIndex].name =
                action.payload.name;
            }
            break;
          case DropDownCategoryEnumModel.SpecialGroup:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let groupIdx = state.userData.data.specialGroups?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (groupIdx !== undefined && groupIdx >= 0) {
              state.userData.data.specialGroups![groupIdx].name =
                action.payload.name;
            }
            break;
          case DropDownCategoryEnumModel.SpecialTag:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let tagIndex = state.userData.data.specialTags?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (tagIndex !== undefined && tagIndex >= 0) {
              state.userData.data.specialTags![tagIndex].name =
                action.payload.name;
            }
            break;
        }

        state.updateCategoryVariableState.category = undefined;
        state.updateCategoryVariableState.variableId = undefined;
        state.updateCategoryVariableState.status = OperationStatus.Completed;
      })
      .addCase(deleteCategoryVariablePending, (state, action) => {
        state.deleteCategoryVariableState.status = OperationStatus.Pending;
      })
      .addCase(deleteCategoryVariableRejected, (state, action) => {
        state.deleteCategoryVariableState.status = OperationStatus.Failed;
        state.deleteCategoryVariableState.message = action.payload;
      })
      .addCase(deleteCategoryVariableFulfilled, (state, action) => {
        switch (action.payload.category) {
          case DropDownCategoryEnumModel.TransactionType:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let typeIndex = state.userData.data.transactionTypes?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (typeIndex !== undefined && typeIndex >= 0) {
              state.userData.data.transactionTypes!.splice(typeIndex, 1);
            }
            break;
          case DropDownCategoryEnumModel.SpecialGroup:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let groupIndex = state.userData.data.specialGroups?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (groupIndex !== undefined && groupIndex >= 0) {
              state.userData.data.specialGroups!.splice(groupIndex, 1);
            }
            break;

          case DropDownCategoryEnumModel.SpecialTag:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let tagIndex = state.userData.data.specialTags?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (tagIndex !== undefined && tagIndex >= 0) {
              state.userData.data.specialTags!.splice(tagIndex, 1);
            }
            break;
          case DropDownCategoryEnumModel.Reason:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let reasonIndex = state.userData.data.reasons?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (reasonIndex !== undefined && reasonIndex >= 0) {
              state.userData.data.reasons!.splice(reasonIndex, 1);
            }
            break;
          case DropDownCategoryEnumModel.Source:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let sourceIndex = state.userData.data.sources?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (sourceIndex !== undefined && sourceIndex >= 0) {
              state.userData.data.sources!.splice(sourceIndex, 1);
            }
            break;
          case DropDownCategoryEnumModel.Area:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let areaIndex = state.userData.data.areas?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (areaIndex !== undefined && areaIndex >= 0) {
              state.userData.data.areas!.splice(areaIndex, 1);
            }
            break;
          case DropDownCategoryEnumModel.CoAgent:
            if (!state.userData.data) {
              state.userData.data = {};
            }
            let coAgentIndex = state.userData.data.coAgents?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (coAgentIndex !== undefined && coAgentIndex >= 0) {
              state.userData.data.coAgents!.splice(coAgentIndex, 1);
            }
            break;
        }
        state.deleteCategoryVariableState.category = action.payload.category;
        state.deleteCategoryVariableState.status = OperationStatus.Completed;
      })
      .addCase(hideCategoryVariablePending, (state, action) => {
        state.hideCategoryVariableState = {
          status: OperationStatus.Pending,
          category: action.payload.category,
          variableId: action.payload.variableId,
        };
      })
      .addCase(hideCategoryVariableFulfilled, (state, action) => {
        state.hideCategoryVariableState = {
          status: OperationStatus.Completed,
          category: action.payload.category,
          variableId: action.payload.variableId,
          hide: action.payload.hide,
        };

        if (!state.userData.data) {
          state.userData.data = {};
        }

        switch (action.payload.category) {
          case DropDownCategoryEnumModel.Reason:
            let reasonIndex = state.userData.data.reasons?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (reasonIndex !== undefined && reasonIndex >= 0) {
              state.userData.data.reasons![reasonIndex].deleted =
                action.payload.hide;
            }
            break;
          case DropDownCategoryEnumModel.TransactionType:
            let transactionIdx =
              state.userData.data.transactionTypes?.findIndex(
                (item) => item.id === action.payload.variableId,
              );
            if (transactionIdx !== undefined && transactionIdx >= 0) {
              state.userData.data.transactionTypes![transactionIdx].deleted =
                action.payload.hide;
            }
            break;
          case DropDownCategoryEnumModel.Source:
            let sourceIndex = state.userData.data.sources?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (sourceIndex !== undefined && sourceIndex >= 0) {
              state.userData.data.sources![sourceIndex].deleted =
                action.payload.hide;
            }
            break;
          case DropDownCategoryEnumModel.Area:
            let areaIndex = state.userData.data.areas?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (areaIndex !== undefined && areaIndex >= 0) {
              state.userData.data.areas![areaIndex].deleted =
                action.payload.hide;
            }
            break;
          case DropDownCategoryEnumModel.CoAgent:
            let coAgentIndex = state.userData.data.coAgents?.findIndex(
              (item) => item.id === action.payload.variableId,
            );
            if (coAgentIndex !== undefined && coAgentIndex >= 0) {
              state.userData.data.coAgents![coAgentIndex].deleted =
                action.payload.hide;
            }
            break;
        }
      });

    // Area Type Preference reducers
    builder
      .addCase(updateAreaTypePreferencePending, (state) => {
        state.areaTypePreferenceState.loading = true;
        state.areaTypePreferenceState.error = null;
      })
      .addCase(updateAreaTypePreferenceFulfilled, (state, action) => {
        state.areaTypePreferenceState.loading = false;
        if (!state.userData.data) {
          state.userData.data = {};
        }
        state.userData.data.preferredAreaType = action.payload;
      })
      .addCase(updateAreaTypePreferenceRejected, (state, action) => {
        state.areaTypePreferenceState.loading = false;
        state.areaTypePreferenceState.error = action.payload;
      })

      // Duplicate candidates
      .addCase(duplicateCandidatesLoading, (state) => {
        state.duplicateCandidates.loading = true;
        state.duplicateCandidates.error = null;
      })
      .addCase(duplicateCandidatesLoaded, (state, action) => {
        state.duplicateCandidates.loading = false;
        state.duplicateCandidates.data = action.payload;
        state.duplicateCandidates.error = null;
      })
      .addCase(duplicateCandidatesError, (state, action) => {
        state.duplicateCandidates.loading = false;
        state.duplicateCandidates.error = action.payload;
      })
      .addCase(resolveDuplicateCandidatePending, (state, action) => {
        state.duplicateCandidates.resolving = true;
        state.duplicateCandidates.resolvingId = action.payload;
        state.duplicateCandidates.resolveError = null;
      })
      .addCase(resolveDuplicateCandidateFulfilled, (state, action) => {
        state.duplicateCandidates.resolving = false;
        state.duplicateCandidates.resolvingId = null;
        // Remove the resolved candidate from the list
        if (state.duplicateCandidates.data) {
          state.duplicateCandidates.data =
            state.duplicateCandidates.data.filter(
              (candidate) => candidate.duplicateCandidateId !== action.payload,
            );
        }
      })
      .addCase(resolveDuplicateCandidateRejected, (state, action) => {
        state.duplicateCandidates.resolving = false;
        state.duplicateCandidates.resolvingId = null;
        state.duplicateCandidates.resolveError = action.payload;
      });
  },
});

export default commonSlice.reducer;
